import { captureMessage } from "@sentry/react";
import { forwardRef, HTMLAttributes, ReactEventHandler, SyntheticEvent, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import { HideAbleImage, StyledPicture } from "./Image.style.js";
import { ImageProps } from "./Image.types.js";

export const Image = forwardRef<
    HTMLPictureElement,
    ImageProps & Omit<HTMLAttributes<HTMLPictureElement>, "width" | "height" | "onLoad" | "children">
>(
    (
        {
            aspectRatio,
            background,
            loader,
            alt,
            fallbackSrc,
            src,
            onLoad,
            lazy,
            dimensions,
            objectFit,
            disableErrorTracking = false,
            noShowLoadingStatusImage = false,
            ...attributes
        },
        ref,
    ) => {
        const pictureRef = useRef<HTMLPictureElement>(null);

        const { inView, ref: InViewRef } = useInView({
            threshold: 0.01,
            delay: 0.25,
            triggerOnce: true,
        });

        useEffect(() => {
            InViewRef(pictureRef.current);
        }, []);

        useImperativeHandle(ref, () => pictureRef.current as HTMLPictureElement);

        const [state, setState] = useState<"loading" | "error" | "loaded">("loading");

        const handleLoad = useCallback(
            (event: SyntheticEvent<HTMLImageElement>) => {
                if (state !== "error") {
                    setState("loaded");
                    if (onLoad) {
                        onLoad({ element: event.target as HTMLImageElement });
                    }
                }
            },
            [onLoad, state],
        );

        const handleError: ReactEventHandler<HTMLImageElement> = useCallback(() => {
            if (state === "loading") {
                setState("error");
                if (!disableErrorTracking) {
                    captureMessage("Image failed to load", { level: "error", extra: { src, fallbackSrc, alt } });
                }
            }
        }, [state]);

        return (
            <StyledPicture
                {...attributes}
                background={background}
                dimensions={{ ...{ maxWidth: "100%" }, ...dimensions }}
                ref={pictureRef}
                aspectRatio={aspectRatio}
                objectFit={objectFit}
            >
                {!noShowLoadingStatusImage && state === "loading" && <div data-element="loader">{loader !== undefined ? loader : "Loading..."}</div>}
                {(!lazy || inView) && (
                    <HideAbleImage
                        hidden={state === "loading"}
                        alt={alt}
                        src={state === "error" ? fallbackSrc : src}
                        onError={handleError}
                        onLoad={handleLoad}
                    />
                )}
            </StyledPicture>
        );
    },
);
